import { resolvedPosFind } from '@common/prosemirror/model/resolved-pos';
import { toggleMultipleChoiceItem } from '@common/prosemirror/transform/multiple-choice';
import { EditorEvent } from '@portal-core/project-files/events/editor-event.event';
import { NodeViewBase } from '@portal-core/text-editor/node-views/node-view.base';
import { GetPosForNodeView } from '@portal-core/text-editor/node-views/types/nodeview-get-pos.type';
import { BidiBridge } from '@portal-core/util/bidi-bridge';
import { ProseMirrorNode } from 'prosemirror-model';
import { Decoration, EditorView, NodeView } from 'prosemirror-view';

export class MultipleChoiceItemNodeView extends NodeViewBase implements NodeView {
  private radioInputDOM: HTMLInputElement;
  private checkboxInputDOM: HTMLInputElement;
  private onRadioChange: (event: Event) => void;
  private onCheckboxChange: (event: Event) => void;

  constructor(
    node: ProseMirrorNode,
    editorView: EditorView,
    getPos: GetPosForNodeView,
    decorations: Decoration[],
    nodeViewBridge: BidiBridge<EditorEvent, EditorEvent>
  ) {
    super(node, editorView, getPos, decorations, nodeViewBridge);

    // Build the DOM structure
    this.radioInputDOM = this.dom.appendChild(document.createElement('input'));
    this.radioInputDOM.classList.add('mc-lwe-multiplechoiceitem-radio-input');
    this.radioInputDOM.type = 'radio';
    this.radioInputDOM.setAttribute('contenteditable', 'false');
    this.radioInputDOM.setAttribute('draggable', 'false');

    this.checkboxInputDOM = this.dom.appendChild(document.createElement('input'));
    this.checkboxInputDOM.classList.add('mc-lwe-multiplechoiceitem-checkbox-input');
    this.checkboxInputDOM.type = 'checkbox';
    this.checkboxInputDOM.setAttribute('contenteditable', 'false');
    this.checkboxInputDOM.setAttribute('draggable', 'false');

    this.contentDOM = this.dom.appendChild(document.createElement('mc-lwe-multiplechoiceitem-content'));

    // Listen to events
    this.onRadioChange = () => this.toggleChecked();
    this.onCheckboxChange = () => this.toggleChecked();

    this.radioInputDOM.addEventListener('change', this.onRadioChange);
    this.checkboxInputDOM.addEventListener('change', this.onCheckboxChange);

    // Sync the node
    this.syncNode(node);
  }

  destroy() {
    this.radioInputDOM?.removeEventListener('change', this.onRadioChange);
    this.checkboxInputDOM?.removeEventListener('change', this.onCheckboxChange);
    super.destroy();
  }

  stopEvent(event: Event): boolean {
    const target = event.target as HTMLElement;
    return target === this.radioInputDOM
      || target === this.checkboxInputDOM;
  }

  syncNode(node: ProseMirrorNode) {
    const checked = !!node.attrs['MadCap:correctAnswer'];
    if (this.radioInputDOM.checked !== checked) {
      this.radioInputDOM.checked = checked;
    }
    if (this.checkboxInputDOM.checked !== checked) {
      this.checkboxInputDOM.checked = checked;
    }

    const checkDisabled = !this.editorView.props?.editable(this.editorView.state);
    this.radioInputDOM.disabled = checkDisabled;
    this.checkboxInputDOM.disabled = checkDisabled;
  }

  private toggleChecked() {
    const resolvePosInfo = resolvedPosFind(this.editorView.state.doc.resolve(this.getPos()), node => node.type.name === 'madcapmultiplechoice');

    if (resolvePosInfo.node) {
      const tr = this.editorView.state.tr;
      toggleMultipleChoiceItem(tr, this.node, this.getPos(), resolvePosInfo.node, resolvePosInfo.$nodePos.pos);

      if (tr.docChanged) {
        this.editorView.dispatch(tr);
      }
    }
  }
}
